import { api } from '../api';
import type {
  AdminMetricsOverview, PlaysPoint, User, Page, Comment, Broadcast, BroadcastAudience, BroadcastCountry, AuditEntry, KnowledgeRequest, KnowledgeRequestStatus, Collection, ClassEpisodesResponse, Payout
} from '../../types/api';

const ADMIN = '/api/admin';

export const ClassesAPI = {
  /** Every creator class (including disabled), newest first, with total episode counts. */
  list: () => api.get<Collection[]>(`${ADMIN}/collections`),
  /** One class by id (admin — includes disabled). */
  get: (id: string) => api.get<Collection>(`${ADMIN}/collections/${encodeURIComponent(id)}`),
  /** A class's full episode list (recorded all-status + live incl. disabled). */
  episodes: (id: string) => api.get<ClassEpisodesResponse>(`${ADMIN}/collections/${encodeURIComponent(id)}/episodes`),
  /** Promote a class to the app's "All" tab hero — only one is featured at a time. */
  setFeatured: (id: string, featured: boolean) =>
    api.post<Collection>(`${ADMIN}/collections/${encodeURIComponent(id)}/featured?featured=${featured}`, undefined, { idempotent: true }),
  /** Disable (hide) or re-enable a whole class. */
  setDisabled: (id: string, disabled: boolean) =>
    api.post<Collection>(`${ADMIN}/collections/${encodeURIComponent(id)}/disabled?disabled=${disabled}`, undefined, { idempotent: true }),
  /** Disable (hide) or re-enable one live episode. */
  setLiveDisabled: (liveId: string, disabled: boolean) =>
    api.post<unknown>(`${ADMIN}/collections/live/${encodeURIComponent(liveId)}/disabled?disabled=${disabled}`, undefined, { idempotent: true }),
};

export const CreatorsAPI = {
  /** Grant creator status to the user with this email (adds CREATOR role + profile). Idempotent. */
  create: (email: string, name?: string, bio?: string) =>
    api.post<{ userId: string }>(`/api/creator/admin/create`, { email, name, bio }, { idempotent: true }),
};

export const PayoutsAPI = {
  /** Payouts, newest first, paginated — optionally filtered by status. */
  list: (status: string | undefined, page: number, size = 25) => {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (status) params.set('status', status);
    return api.get<Page<Payout>>(`${ADMIN}/payouts?${params.toString()}`);
  },
  /** Approve a PENDING payout → fires the provider transfer. */
  approve: (id: string) => api.post<Payout>(`${ADMIN}/payouts/${encodeURIComponent(id)}/approve`, undefined, { idempotent: true }),
  /** Reject a payout (no transfer). */
  reject: (id: string) => api.post<Payout>(`${ADMIN}/payouts/${encodeURIComponent(id)}/reject`, undefined, { idempotent: true }),
  /** Stage yesterday's payouts now (off-cycle/testing). */
  runNow: () => api.post<number>(`${ADMIN}/payouts/run`, undefined, { idempotent: true }),
};

export const AdminAPI = {
  overview: () => api.get<AdminMetricsOverview>(`${ADMIN}/metrics/overview`),
  plays: (window: '24h' | '7d' | '30d' = '30d') =>
    api.get<PlaysPoint[]>(`${ADMIN}/metrics/plays?window=${window}`),
  health: () => api.get<{ status: 'ok' | 'degraded'; checks: Record<string, boolean> }>(`${ADMIN}/health`),
};

export const UsersAPI = {
  list: (q: string, page: number, size = 25, role?: string) => {
    const params = new URLSearchParams({ q, page: String(page), size: String(size) });
    if (role) params.set('role', role);
    return api.get<Page<User>>(`${ADMIN}/users?${params.toString()}`);
  },
  get: (id: string) => api.get<User>(`${ADMIN}/users/${encodeURIComponent(id)}`),
  suspend: (id: string, reason: string) =>
    api.post<void>(`${ADMIN}/users/${encodeURIComponent(id)}/suspend`, { reason }, { idempotent: true }),
  unsuspend: (id: string, reason: string) =>
    api.post<void>(`${ADMIN}/users/${encodeURIComponent(id)}/unsuspend`, { reason }, { idempotent: true }),
  refreshCache: (id: string, reason: string, originalTransactionIds?: string[]) =>
    api.post<{ clearedAt: string; evicted: string[] }>(
      `${ADMIN}/users/${encodeURIComponent(id)}/refresh-cache`,
      { reason, originalTransactionIds }, { idempotent: true }),
  setRoles: (id: string, roles: string[], reason: string) =>
    api.post<void>(`${ADMIN}/users/${encodeURIComponent(id)}/roles`, { roles, reason }, { idempotent: true }),
};

export interface GoogleReconcileChange {
  subscriptionId: string;
  playState: string;
  expiresOn?: { from: string; to: string };
  cancelled?: { from: boolean; to: boolean };
}

export interface GoogleReconcilePage {
  applied: boolean;
  total: number;
  offset: number;
  nextOffset: number | null;
  counts: Record<string, number>;
  changes: GoogleReconcileChange[];
  errors: { subscriptionId: string; message: string }[];
}

export const SubscriptionsAPI = {
  gift: (email: string, plan: string, durationDays: number) =>
    api.post<void>(`${ADMIN}/subscriptions/gift`, { email, plan, durationDays }, { idempotent: true }),
  /** Re-read one page of Play subscriptions from Google. Writes nothing unless apply is true. */
  reconcileGoogle: (apply: boolean, offset: number, limit = 100) =>
    api.post<GoogleReconcilePage>(`${ADMIN}/subscriptions/google/reconcile`, { apply, offset, limit }),
};

export const ModerationAPI = {
  comments: (status: 'FLAGGED' | 'REMOVED' | 'OK', page: number, size = 25) => {
    const params = new URLSearchParams({ status, page: String(page), size: String(size) });
    return api.get<Page<Comment>>(`${ADMIN}/moderation/comments?${params.toString()}`);
  },
  act: (id: string, action: 'approve' | 'remove' | 'restore', reason: string) =>
    api.post<void>(`${ADMIN}/moderation/comments/${encodeURIComponent(id)}/${action}`, { reason }, { idempotent: true }),
};

export interface BroadcastSendPayload {
  title: string;
  content: string;
  audience: BroadcastAudience;
  userEmail?: string;
  userDeviceId?: string;
  country?: BroadcastCountry;
}

export const BroadcastsAPI = {
  list: (page: number, size = 25) =>
    api.get<Page<Broadcast>>(`${ADMIN}/broadcasts?page=${page}&size=${size}`),
  send: (payload: BroadcastSendPayload) =>
    api.post<Broadcast>(`${ADMIN}/broadcasts`, payload, { idempotent: true }),
};

export const RequestsAPI = {
  list: (status: KnowledgeRequestStatus, page: number, size = 25) => {
    const p = new URLSearchParams({ status, page: String(page), size: String(size) });
    return api.get<Page<KnowledgeRequest>>(`${ADMIN}/requests?${p.toString()}`);
  },
  get: (id: string) => api.get<KnowledgeRequest>(`${ADMIN}/requests/${encodeURIComponent(id)}`),
  reply: (id: string, payload: { title: string; content: string }) =>
    api.post<KnowledgeRequest>(`${ADMIN}/requests/${encodeURIComponent(id)}/reply`, payload, { idempotent: true }),
};

export const AuditAPI = {
  list: (page: number, size = 50, actor?: string) => {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (actor) params.set('actor', actor);
    return api.get<Page<AuditEntry>>(`${ADMIN}/audit?${params.toString()}`);
  },
};
